import { MembershipProductType, MSSMemberStatus, RoleType } from '@ace-clublabs/common-membership-api-types';
import { useGlobalState } from '../../../state/state';
import { getMembershipTitle, capitalizeString } from '../../../utils/stringHelper';
import { ConfirmationCard, ConfirmationItemDetails } from '../ConfirmationCard';
import { IGetConfirmationResponse, MSSProductStatus } from '@lib/types';
import { Box } from '@ace-clublabs/shared-component-library';

export const ConfirmationSummary = () => {
  const [memberDetails] = useGlobalState('memberDetails');
  const [confirmation] = useGlobalState('confirmation');
  const { updatedBenefitLevel, currentBenefitLevel, benefitLevelEffectiveDate, products, members, effectiveDate } =
    confirmation as IGetConfirmationResponse;
  const { inRenewal, inArrears } = memberDetails;
  const hasUpdatedBenefitLevel = updatedBenefitLevel !== currentBenefitLevel;

  const benefitLevelTitle = getMembershipTitle(
    memberDetails,
    hasUpdatedBenefitLevel ? updatedBenefitLevel : currentBenefitLevel,
    confirmation
  );

  const addedMembers = members?.filter(
    (member) => member.status === MSSMemberStatus.ADDED && member.roleType !== RoleType.PRIMARY
  );
  const addedMemberNames = addedMembers
    ?.map(({ firstName, lastName }) => capitalizeString(firstName) + ' ' + capitalizeString(lastName))
    .join(', ');

  const associateMembers = members?.filter((member) => member.roleType !== RoleType.PRIMARY);

  const allAssociateMemberNames = associateMembers
    ?.filter(({ status }) => status !== MSSMemberStatus.REMOVED)
    ?.map(({ firstName, lastName }) => capitalizeString(firstName) + ' ' + capitalizeString(lastName))
    .join(', ');

  const primaryMember = members?.find((member) => member.roleType === RoleType.PRIMARY);

  const primaryMemberName =
    capitalizeString(primaryMember?.firstName) + ' ' + capitalizeString(primaryMember?.lastName);

  const addedRV = products?.filter(
    (product) => product.status === MSSProductStatus.ADDED && product.type === MembershipProductType.RV
  );

  const hasOrAddedRV = products?.filter(
    (product) => product.status !== MSSProductStatus.REMOVED && product.type === MembershipProductType.RV
  );

  const rvEffectiveDate = products?.find((product) => product.type === MembershipProductType.RV)?.effectiveDate;

  const getMidtermConfirmationSummaryItems = () => {
    const summaryItems: ConfirmationItemDetails[] = [];
    if (hasUpdatedBenefitLevel) {
      summaryItems.push({
        title: 'Benefit Level:',
        details: <>{benefitLevelTitle} member</>,
        effectiveDate: benefitLevelEffectiveDate,
        className: 'benefit-level',
      });
    }

    if (addedMembers && addedMembers?.length > 0) {
      summaryItems.push({
        title: 'Family member added:',
        details: addedMemberNames,
        className: 'members-added',
        downloadMembershipCard: true,
        mask: true,
      });
    }

    if (addedRV && addedRV.length > 0) {
      summaryItems.push({
        title: 'Add-ons:',
        details: 'Roadside service for RV/motorcycle',
        effectiveDate: rvEffectiveDate,
        className: 'add-ons',
      });
    }
    return summaryItems;
  };

  const getAllConfirmationSummaryItems = () => {
    const summaryItems: ConfirmationItemDetails[] = [
      {
        title: 'Membership renews on:',
        details: effectiveDate,
        className: 'renewal-date',
      },
      {
        title: 'Benefit Level:',
        details: <>{benefitLevelTitle} member</>,
        effectiveDate: benefitLevelEffectiveDate,
        className: 'benefit-level',
      },
      {
        title: 'Primary member:',
        details: primaryMemberName,
        className: 'primary-member',
        mask: true,
      },
    ];

    if (associateMembers && associateMembers?.length > 0) {
      if (addedMembers && addedMembers?.length > 0) {
        summaryItems.push({
          title: 'Family members:',
          details: allAssociateMemberNames,
          className: 'family-members',
          downloadMembershipCard: true,
          mask: true,
        });
      } else {
        summaryItems.push({
          title: 'Family members:',
          details: allAssociateMemberNames,
          className: 'family-members',
          mask: true,
        });
      }
    }

    if (hasOrAddedRV && hasOrAddedRV.length > 0) {
      summaryItems.push({
        title: 'Add-ons:',
        details: 'Roadside service for RV/motorcycle',
        effectiveDate: rvEffectiveDate,
        className: 'add-ons',
      });
    }

    return summaryItems;
  };

  return (
    <Box className="confirmation-summary-container">
      <ConfirmationCard
        cardTitle="Summary"
        items={inRenewal || inArrears ? getAllConfirmationSummaryItems() : getMidtermConfirmationSummaryItems()}
      />
    </Box>
  );
};
